import React, { useState } from "react";
import { useRecoilValue } from 'recoil';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from 'prop-types'; 

import { Container, Typography, TextField, Button, List, ListItem, ListItemText, IconButton, Dialog, DialogActions, DialogContent, DialogTitle, Box, Pagination, Divider } from "@mui/material";
import Grid from '@mui/material/Grid2';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faCommentDots } from "@fortawesome/free-solid-svg-icons";

import { userInfo } from "../../auth/AuthProvider";
import Dialog4Confirm from "./Dialog4Confirm"
import Dialog4Alert from "./Dialog4Alert";
import { Transition } from "./ControlUtils";
import { useBoardCommentListQuery, useBoardCommentItemQuery, useBoardCommentItemPostMutation, useBoardCommentItemPutMutation, useBoardCommentItemDeleteMutation } from "../../hooks/useBoard/useBoardCommentQuery";
import { CheckIsWriter } from "./ControlUtils";
import { isDebug, utils } from "../../utils";

/*
  Comment 정보
    CommentId, BoardId, CreatedByName, CommentBody, CreatedDate, CreatedBy
*/


export default function ControlComment(props) {
    const navigate = useNavigate();
    const auth = useRecoilValue(userInfo);
    const { t } = useTranslation();
    const { boardId, refMessage, maxWidth } = props;
    const isLoadingRef = React.useRef(false);
    const refConfirm = React.useRef(null);
    const refAlert = React.useRef(null);
    const lineColor = 'rgb(155, 149, 152)';

    const [paginationModel, setPaginationModel] = React.useState({
      Page: 1,
      PageSize: process.env.REACT_APP_BOARD_COMMENT_VIEW_COUNT,
      BoardId: boardId,
      PageCount: 0,
    });
    const defaultValue = {
      CommentId: 0,
      BoardId: boardId,
      CreatedByName: "",
      CommentBody: "",
      CreatedBy: auth.UserId,
    }; //CreatedDate: new Date().toISOString(),
    const [posts, setPosts] = useState([]);
    const [newPost, setNewPost] = useState(defaultValue);
    const [editPost, setEditPost] = useState(defaultValue);
    const [openDialog, setOpenDialog] = useState(false);
    const { mutate: postBoardCommentItem } = useBoardCommentItemPostMutation();
    const { mutate: putBoardCommentItem } = useBoardCommentItemPutMutation();
    const { mutate: deleteBoardCommentItem } = useBoardCommentItemDeleteMutation();

    const { error: itemArticleError, data: itemArticleData } = useBoardCommentItemQuery(editPost.BoardId, editPost.CommentId);
    const { isError, isLoading, isFetching, isSuccess, error, data: commentList, refetch } = useBoardCommentListQuery(paginationModel);

  // 게시글 추가 핸들러
  const handleAddPost = () => {
    if(auth.userId === undefined || auth.userId === null || auth.userId === '') {
      refMessage.openMessage(`${t('error.notlogin')}`);
      setTimeout(() => {
          navigate(`/login`);
      }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
    }
    else if (newPost?.CommentBody?.trim()) {
      refConfirm.current.showMessageBox(`${t('button.save')}`, `${t('alert.saveconfirm')}`, (result) => {
        if(result) {
          if (isLoadingRef.current)  return;
          isLoadingRef.current = true;

          postBoardCommentItem(newPost, {
            onSuccess: () => {
              refAlert.current.showMessageBox(t('button.save'), t('message.savecomplet'));
            },
          });
                
          isLoadingRef.current = false;
          setNewPost(defaultValue); // 입력란 초기화
        }
      });
    }
  };

  // 게시글 수정 핸들러
  const handleEditPost = () => {
    if(auth.userId === undefined || auth.userId === null || auth.userId === '') {
      refMessage.openMessage(`${t('error.notlogin')}`);
      setTimeout(() => {
          navigate(`/login`);
      }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
    }
    else if (editPost?.CommentBody?.trim()) {
      refConfirm.current.showMessageBox(`${t('button.save')}`, `${t('alert.saveconfirm')}`, (result) => {
        if(result)
        {
          if (isLoadingRef.current)  return;
          isLoadingRef.current = true;
          
          if(editPost.CommentId > 0) {
            putBoardCommentItem(editPost, {
              onSuccess: () => {
                refAlert.current.showMessageBox(t('button.save'), t('message.updatecomplet'));
              },
              onError: (err) => {
                let msg = err?.response?.data.detail[0].msg || `${t('error.notprosess')}`;
                refAlert.current.showMessageBox(t('error.title'), msg);
              },
            });
          }

          isLoadingRef.current = false;
          setOpenDialog(false); // 다이얼로그 닫기
          setEditPost(defaultValue);
          refetch();
        }
      });
    }
  };

  // 게시글 삭제 핸들러
  const handleDeletePost = (commentid) => {
    if(auth.userId === undefined || auth.userId === null || auth.userId === '') {
      refMessage.openMessage(`${t('error.notlogin')}`);
      setTimeout(() => {
          navigate(`/login`);
      }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
    }
    else if(commentid > 0) {
      refConfirm.current.showMessageBox(`${t('button.delete')}`, `${t('alert.delete')}`, (result) => {
        if(result) {
          if (isLoadingRef.current)  return;
          isLoadingRef.current = true;

          // 삭제하고
          deleteBoardCommentItem(commentid, {
            onSuccess: () => {
              refAlert.current.showMessageBox(t('button.delete'), t('message.deletecomplet'));
            },
          });

          isLoadingRef.current = false;
          refetch();
        }
      });
    }
  };

  // 수정 버튼 클릭시 다이얼로그 열기
  const handleOpenDialog = (editObject) => {
    if(auth.userId === undefined || auth.userId === null || auth.userId === '') {
      refMessage.openMessage(`${t('error.notlogin')}`);
      setTimeout(() => {
          navigate(`/login`);
      }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
    }
    else {
      setEditPost(editObject);
      setOpenDialog(true);
    }
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditPost(defaultValue);
  };

  
  React.useEffect(() => {
    try {
      if(commentList !== undefined && commentList !== null)
      {
        setPosts(commentList?.data?.Results||[]);

        let pageCount = parseInt((commentList?.data?.TotalCnt||1) / (commentList?.data?.PageSize||1))
        if(((commentList?.data?.TotalCnt||1) % (commentList?.data?.PageSize||1)) > 0) pageCount++;
        setPaginationModel({...paginationModel, PageCount: pageCount});
      }
    }
    catch(err)
    {
      console.log('No Data', new Date().toISOString(), paginationModel, err);
    }

    return () => {
    } 
  }, [isError, isLoading, isFetching, isSuccess, error, commentList]);
            
  React.useEffect(() => {
    try {
      if (itemArticleData !== undefined && itemArticleData !== null && itemArticleData.data !== null) {
        if(itemArticleData.data.BoardId === editPost.BoardId && itemArticleData.data.CommentId === editPost.CommentId) {
          setEditPost(itemArticleData.data);
        }
        else {
          handleCloseDialog();
        }
      }
    }
    catch(err)
    {
        console.log(new Date().toISOString(), itemArticleData, err);
    }
  }, [itemArticleError, itemArticleData]);


  return (
    <Container variant="section" maxWidth={maxWidth || "md"} style={{ marginTop: "2px", borderBottom: `1px dotted ${lineColor}`, paddingTop: "10px" }}>

      <Typography variant="h6" gutterBottom align="center">
        <FontAwesomeIcon icon={faList} style={{ paddingLeft: '0px', paddingRight: '10px' }} />
        {t('others.commentlist')}
      </Typography>

      {/* 댓글 작성 */}
      <Divider sx={{ my: 1, borderStyle: "dotted", borderColor: lineColor }} />
      <Container sx={{ p: 1, width: '100%' }}>
        <Grid container spacing={1}>
          <Grid size={{ xs:12, sm: 12, md: 10, lg: 10, xl: 10 }} sx={{ paddingRight: '2px' }}>
            <TextField
              label={t('field.commentbody')}
              variant="outlined"
              size="small" 
              fullWidth
              value={newPost.CommentBody}
              onChange={(e) => setNewPost({ ...newPost, CommentBody: e.target.value})}
            />
          </Grid>
          <Grid size={{ xs:12, sm: 12, md: 2, lg: 2, xl: 2 }} sx={{ paddingLeft: '2px' }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<SaveIcon />}
              onClick={handleAddPost}
              disabled={!auth.userId}
            >
              {t('button.registration')}
            </Button>
          </Grid>
        </Grid>
      </Container>
      
      {/* 댓글 내용 출력 */}
      <Divider sx={{ my: 1, borderStyle: "dotted", borderColor: lineColor }} />
      <Container sx={{ p: 1, width: '100%'}}>
        <List sx={{ p: 0, m: 0, width: '100%' }}>
          {posts?.map((post, index) => (
            <ListItem key={post.RowNum} sx={{ p: 0, m: 0, paddingLeft: '8px', paddingTop: '4px', marginBottom: '4px',  borderBottom: '1px dotted #ccc', }}>
              <ListItemText>
                {isDebug() ? `[${utils.date.dateToString(post.CreatedDate, "YYYY-MM-DD HH:mm")}] [${post.RowNum}]` : `[${utils.date.dateToString(post.CreatedDate, "YYYY-MM-DD HH:mm")}]`}
                <FontAwesomeIcon icon={faCommentDots} style={{ paddingLeft: '5px', paddingRight: '5px' }} />
                {post.CommentBody}
              </ListItemText>
              <IconButton edge="end" onClick={() => handleOpenDialog(post)} disabled={!CheckIsWriter(auth, post.CreatedBy)} >
                <EditIcon />
              </IconButton>
              <IconButton edge="end" onClick={() => handleDeletePost(post.CommentId)} disabled={!CheckIsWriter(auth, post.CreatedBy)} >
                <DeleteIcon />
              </IconButton>
            </ListItem>
          ))}
        </List>
      </Container>

      {/* 페이징 적용 */}
      <Divider sx={{ my: 1, borderStyle: "dotted", borderColor: '#efefef' }} />
      <Box display="flex" justifyContent="center" my={2}>
        <Pagination size="small" defaultPage={paginationModel.Page} count={paginationModel.PageCount} variant="outlined" color="primary" shape="rounded" showFirstButton showLastButton onChange={(e, page) => { if (paginationModel.Page !== page) { setPaginationModel({...paginationModel, Page: page}); } }} />
      </Box>

      {/* 메시지창 적용 */}
      <Dialog4Confirm ref={refConfirm} />
      <Dialog4Alert ref={refAlert} />

      {/* 게시글 수정 다이얼로그 */}
      <Dialog open={openDialog} TransitionComponent={Transition} fullWidth={true} maxWidth={"md"} onClose={handleCloseDialog}>
        <DialogTitle>{t('field.commentbody')} {t('button.modify')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('field.commentbody')}
            variant="outlined"
            fullWidth
            value={editPost.CommentBody}
            onChange={(e) => setEditPost({ ...editPost, CommentBody: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" disabled={!auth.userId}>
            {t('button.cancel')}
          </Button>
          <Button onClick={handleEditPost} color="primary" disabled={!auth.userId}>
            {t('button.modify')}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

ControlComment.propTypes = {
  boardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, 
  refMessage: PropTypes.object, 
  maxWidth: PropTypes.oneOf(['md', 'lg', 'xl', 'xs', 'sm']),
};